import {
  usePresentationFileSubscription,
  PresentationFileFragment,
  PresentationFileFragmentDoc,
} from '@sketch/gql-types'
import { RenderStatus } from 'utils/renderStatus'
import { dataIdFromObject } from '@sketch/graphql-cache'

/**
 * Sending the getPagePresentationFile query sends to the server a request to get the presentation file.
 * If the presentation file is ready then we can use it straight away and we don't need this subscription.
 * If not available yet, the backend starts generating the presentation file and this subscription allows
 * us to receive an event to update the frontend when the Presentation file is ready.
 */
export function usePrFileStatusChangesSubscriptionIfNeeded(
  documentRenderStatus: RenderStatus | null,
  presentationFile: PresentationFileFragment['presentationFile'] | undefined,
  pageIdentifier: string | undefined
) {
  const shouldSubscribeToPrFileStatusChanges =
    Boolean(pageIdentifier) &&
    documentRenderStatus === 'ready' &&
    presentationFile?.status === 'PENDING'

  usePresentationFileSubscription({
    variables: { pageId: pageIdentifier ?? '' },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const pageCacheId = dataIdFromObject({
        __typename: 'Page',
        identifier: pageIdentifier,
      })

      const newDownloadUrl = subscriptionData.data?.presentationFile.url

      if (pageCacheId && newDownloadUrl) {
        // Fragment contains more information than is returned from the
        // subscriptionData now, so we need to read it before re-writing it.
        const frag = client.readFragment<PresentationFileFragment>({
          fragment: PresentationFileFragmentDoc,
          id: pageCacheId,
          fragmentName: 'PresentationFile',
        })

        if (frag && frag.presentationFile) {
          client.writeFragment<PresentationFileFragment>({
            fragment: PresentationFileFragmentDoc,
            id: pageCacheId,
            data: {
              ...frag,
              presentationFile: {
                ...frag.presentationFile,
                downloadUrl: newDownloadUrl,
                status: 'COMPLETED',
              },
            },
          })
        }
      }
    },
    skip: !shouldSubscribeToPrFileStatusChanges,
  })
}
